import React, { useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';

import { useTeamQuery } from '../../graphql/team/queryTeam';

import { EVENT } from '../../constants/events';

import { EXTERNAL_GOOGLE_ADS_MANAGEMENT_URL, PATHS } from '../../routes/paths';
import SvgPortfolio from 'components/icons/Portfolio';

import { sizes } from '../../theme/device';
import * as Styles from './styles/Sidebar';

import ButtonBase from '../ButtonBase';
import { pathIncludes } from '../ButtonNav';
import { Logo } from '../Logo';
import { LaptopLUp } from '../MediaQuery';
import Account from './components/Account';
import SvgBullseyePointer from '../icons/BullseyePointer';
import SvgCog from '../icons/Cog';
import SvgFileChartLine from '../icons/FileChartLine';
import SvgHomeAlt from '../icons/HomeAlt';
import SvgQuestionCircle from '../icons/QuestionCircle';
import PortfoliosSearchSidebarItem, { SearchComponentType } from './PortfolioSearchSidebarItem';
import SidebarItem from './SidebarItem';
import Teams from './Teams';
import TooltipWrapForSmallScreens from './TooltipWrapForSmallScreens';
import { Car, ChevronLeft, ChevronRight, ExternalLink, TrendingUp } from 'lucide-react';
import { KFZ_COCKPIT_TEAM_ID } from '../../configs/kfzCockpit';
import { useTeam } from '../../context/TeamProvider';
import { FEATURE_FLAGS } from '../../constants/featureFlags';
import { useSidebar } from '../../context/SidebarProvider';
import { cn } from '../../lib/utils';
import { Button } from '../../components-ui/Button';
import { Skeleton } from '../../components-ui/Skeleton';
import SvgNexoyaLogo from '../icons/NexoyaLogo';
import useUserStore from '../../store/user';
import useOrganizationStore from '../../store/organization';
import SvgAttributions from '../icons/Attributions';
import { useCan } from '../../Auth/rbac';
import { useEntitlement, useIsFeatureLocked } from '../../entitlements/useEntitlement';
import { SetupChecklistSection } from '../../onboarding/checklist/SetupChecklistSection';
import { useTrendSignalsFlag } from '../../hooks/useTrendSignalsEnabled';

const SIDEBAR_ITEMS = [
  {
    name: (
      <div>
        Welcome <span style={{ marginLeft: 4 }}>👋</span>
      </div>
    ),
    cy: 'welcomeBtnNav',
    link: PATHS.APP.ONBOARD_GUIDE,
    exact: true,
    isOnboarding: true,
  },
  {
    name: 'Dashboard',
    cy: 'dashboardBtnNav',
    link: PATHS.APP.HOME,
    exact: true,
    icon: <SvgHomeAlt />,
  },
  {
    name: 'Portfolios',
    cy: 'portfoliosBtnNav',
    link: PATHS.APP.PORTFOLIOS,
    event: EVENT.ROUTE_PORTFOLIOS,
    icon: <SvgPortfolio />,
    component: SearchComponentType.PORTFOLIOS,
  },
  {
    name: 'Attributions',
    cy: 'attributionsBtnNav',
    link: PATHS.APP.ATTRIBUTIONS,
    event: EVENT.ROUTE_ATTRIBUTION,
    icon: <SvgAttributions />,
    feature: FEATURE_FLAGS.ATTRIBUTION_MODEL,
  },
  {
    name: 'Reports',
    cy: 'reportsBtnNav',
    link: PATHS.APP.REPORTS,
    icon: <SvgFileChartLine />,
    event: EVENT.ROUTE_REPORTS,
  },
  {
    name: 'Metrics',
    cy: 'metricsBtnNav',
    link: PATHS.APP.KPIS,
    icon: <SvgBullseyePointer />,
    isActive: pathIncludes('/content/'),
  },
  {
    name: 'Trend Signals',
    cy: 'trendSignalsBtnNav',
    link: PATHS.APP.TREND_SIGNALS,
    event: EVENT.ROUTE_TREND_SIGNALS,
    icon: <TrendingUp className="h-5 w-5 text-neutral-700" strokeWidth={1.7} />,
  },
  {
    name: 'Campaign Manager',
    link: EXTERNAL_GOOGLE_ADS_MANAGEMENT_URL,
    feature: FEATURE_FLAGS.GOOGLE_ADS_CAMPAIGN_MANAGEMENT,
    icon: <ExternalLink className="h-4 w-4 text-neutral-700" />,
    event: EVENT.ROUTE_CAMPAIGN_MANAGER,
    external: true,
  },
];

const LOWER_SIDEBAR_ITEMS = [
  {
    name: 'Settings',
    cy: 'settings',
    link: PATHS.APP.SETTINGS,
    icon: <SvgCog />,
    isActive: pathIncludes('/settings/'),
  },
];

function SidebarSkeleton() {
  return (
    <div className="flex flex-col">
      {/* Main navigation items skeleton */}
      <div className="flex flex-col gap-2 px-3">
        {[1, 2, 3].map((index) => (
          <div key={index} className="flex items-center gap-3 px-4 py-2.5">
            {/* Icon skeleton */}
            <Skeleton className="h-5 w-5 rounded" />
            {/* Text skeleton */}
            <Skeleton className="h-5 flex-1 rounded" />
          </div>
        ))}
      </div>

      {/* Lower sidebar items skeleton */}
      <div className="mt-auto flex flex-col gap-2 px-3">
        {[1].map((index) => (
          <div key={index} className="flex items-center gap-3 px-4 py-2.5">
            {/* Icon skeleton */}
            <Skeleton className="h-5 w-5 rounded" />
            {/* Text skeleton */}
            <Skeleton className="h-4 flex-1 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

function Sidebar() {
  const { teamId } = useTeam();
  const { user, isSupportUser } = useUserStore();
  const { getHelpPageUrl } = useOrganizationStore();

  const canSeeSettings = useCan('team.settings.view');
  const lowerItems = canSeeSettings
    ? LOWER_SIDEBAR_ITEMS
    : LOWER_SIDEBAR_ITEMS.filter((item) => item.name !== 'Settings');

  const { data: teamData, loading } = useTeamQuery({
    withMembers: false,
    withOrg: false,
  });

  const attributionAccess = useEntitlement('attribution.view');
  // `denied`, not "anything short of granted" as Attributions uses below: these two are core
  // navigation for every paying team, and hiding them for the frame before the plan lands
  // would reflow the rail on every page load. Attributions can afford strictness because the
  // support-only rule already keeps it off every customer seat.
  const metricsLocked = useIsFeatureLocked('metrics.view');
  const { enabled: trendSignalsEnabled } = useTrendSignalsFlag();

  const { sidebarWidth, isCollapsed, toggleCollapse } = useSidebar();

  // Computed during render, not in a layout effect: the plan `attributionAccess` reads is
  // published from a PASSIVE effect in `PrivateApp` (`usePlanSync`), and React flushes every
  // layout effect for a commit before any passive effect — so an effect keyed on
  // `[teamData, user]` would see a stale plan on the publishing commit and never re-run. It
  // also drops the effect-plus-setState loop that forced a reference-stable query result.
  const filteredItems = useMemo(() => {
    // Applied before the early return too, so the base list never flashes a flag-off entry.
    const baseItems = trendSignalsEnabled
      ? SIDEBAR_ITEMS
      : SIDEBAR_ITEMS.filter((sidebarItem) => sidebarItem.name !== 'Trend Signals');

    // Before the team query answers, keep the previous behaviour of showing the base list.
    if (loading || !user) return baseItems;

    const isOnboarding = teamData?.team?.onboarding?.onboardingTasks?.length;
    const hasCustomDashboardLinks = teamData?.team?.dashboardUrls?.length;

    let items = baseItems;

    if (isOnboarding && !isSupportUser) {
      items = items.filter((sidebarItem) => sidebarItem.isOnboarding);
    } else {
      items = items.filter((sidebarItem) => !sidebarItem.isOnboarding);
    }

    if (!hasCustomDashboardLinks) {
      items = items.filter((sidebarItem) => sidebarItem.name !== 'Dashboard');
    }

    // Attribution is Full-only (`features.ts`, `attribution.view`). The support-only rule
    // below already hides it from every customer seat; the entitlement adds the case it
    // cannot see — a support user PREVIEWING Nexoya Starter, whose bypass `useEntitlement`
    // suspends while an override is active. Anything short of `granted` hides it, so an
    // unresolved plan never flashes a Full-only entry onto a Starter rail.
    if (!isSupportUser || attributionAccess !== 'granted') {
      items = items.filter((sidebarItem) => sidebarItem.name !== 'Attributions');
    }

    if (metricsLocked) {
      items = items.filter((sidebarItem) => sidebarItem.name !== 'Metrics');
    }

    return items;
  }, [teamData, user, isSupportUser, loading, attributionAccess, metricsLocked, trendSignalsEnabled]);

  const isBelowLaptopL = useMediaQuery({
    maxWidth: sizes.laptopL - 1,
  });

  return (
    <Styles.WrapStyled className="group relative" isCollapsed={isCollapsed} sidebarWidth={sidebarWidth}>
      <div className={cn('logo-wrap flex', isCollapsed ? 'justify-center' : 'justify-between')}>
        {isCollapsed ? (
          <SvgNexoyaLogo style={{ width: 24, height: 24 }} />
        ) : (
          <Logo hideName={isBelowLaptopL || isCollapsed} />
        )}

        {!isBelowLaptopL ? (
          <Button
            style={{ filter: 'drop-shadow(0px 4px 8px rgba(138, 140, 158, 0.25))' }}
            className="absolute right-[-10px] top-[54px] z-10 h-5 w-5 rounded-full opacity-0 transition-opacity hover:bg-neutral-50 hover:text-neutral-600 group-hover:opacity-100"
            onClick={toggleCollapse}
            variant="outline"
            size="icon"
          >
            {isCollapsed ? (
              <ChevronRight className={cn('h-[20px] w-[20px]', isBelowLaptopL ? 'opacity-0' : 'opacity-100')} />
            ) : (
              <ChevronLeft className={cn('h-[20px] w-[20px]', isBelowLaptopL ? 'opacity-0' : 'opacity-100')} />
            )}
          </Button>
        ) : null}
      </div>
      <Teams className="team-wrap" />
      <Styles.ScrollbarStyled className="nav-wrap">
        <div>
          {loading ? (
            <SidebarSkeleton />
          ) : (
            filteredItems.map((item) =>
              item?.component === SearchComponentType.PORTFOLIOS ? (
                <PortfoliosSearchSidebarItem key={item.link} item={item} />
              ) : (
                // @ts-expect-error
                <SidebarItem key={item!.link} item={item} />
              ),
            )
          )}
          {!loading && teamId === KFZ_COCKPIT_TEAM_ID && (
            <>
              <hr className="mx-3 my-4 border-neutral-300" />
              <SidebarItem
                item={{
                  name: 'KFZ Cockpit',
                  cy: 'kfzCockpitBtnNav',
                  link: PATHS.APP.KFZ_COCKPIT,
                  icon: <Car className="h-5 w-5 text-neutral-700" aria-hidden="true" />,
                }}
              />
            </>
          )}
        </div>
        <div>
          {/* First child of the bottom cluster, so `ScrollbarStyled` keeps exactly two children
              and `space-between` still means top/bottom: the band grows upward while
              Settings/Help/Account stay pinned. */}
          <SetupChecklistSection />
          {!loading ? lowerItems.map((item) => <SidebarItem key={item.link} item={item} />) : null}
          <Styles.SupportWrapStyled className="support-wrap">
            <TooltipWrapForSmallScreens content="Help" variant="dark" placement="right">
              <ButtonBase data-cy="helpBtnNav" href={getHelpPageUrl()} target="_blank" rel="noopener noreferrer">
                <SvgQuestionCircle className="fill-neutral-700" style={{ width: 19.2, height: 19.2 }} />
                <LaptopLUp>
                  <span className="font-light text-neutral-700">Help</span>
                </LaptopLUp>
              </ButtonBase>
            </TooltipWrapForSmallScreens>
          </Styles.SupportWrapStyled>

          <Account />
        </div>
      </Styles.ScrollbarStyled>
    </Styles.WrapStyled>
  );
}

export default Sidebar;
